// ============================================================================
// silence-pre-auth-rejections.js  (v5.13.7)
// ----------------------------------------------------------------------------
// Synchronous, module-import-time installer for the unhandledrejection swallow
// that hides 401/403 promise rejections from the console.
//
// Why this lives here, not in App.jsx:
//   Many components fire fire-and-forget API reads (project summaries,
//   learnings, CRM insights) during their first useEffect. React runs child
//   useEffects BEFORE the parent's, so even if App.jsx installs the listener
//   in its own useEffect, the rejection has already fired by then.
//
//   By doing the installation at module-evaluation time (synchronous side
//   effect at import-top), we beat every component's lifecycle: as long as
//   main.jsx imports this file BEFORE App, the listener is in place before
//   any React component mounts.
//
// What we swallow:
//   ONLY promise rejections that BOTH originate from apiFetch
//   (`.reason.qmSource === 'apiFetch'`) AND carry a 401/403 status. Every
//   other error — including 401/403 from non-apiFetch code paths — still
//   surfaces normally so genuine bugs remain visible AND the session-expired
//   toast in App.jsx can react to manually-thrown 401s.
//
// History: Pre-v5.13.7 this guard swallowed every 401/403 app-wide. Audit
// finding F-02 showed that broke session-expiry UX (the user got no banner
// because data-service.js:isUnreachableError() also excludes 401/403 from
// the connectivity classifier). Tagging apiFetch errors with `qmSource`
// lets us silence the boot-time noise without muting the session-expired
// pathway.
//
// Idempotent: guarded by a `__qm_unhandledrejection_swallow_installed`
// property on `window` so HMR / dev re-runs don't stack listeners.
// ============================================================================

// `window as any` rather than a `declare global` block: this file is a
// side-effect import with no top-level import or export, so it is a SCRIPT, not
// a module, and a global augmentation is illegal here. Adding `export {}` to
// make it a module would change what esbuild emits, which the emit-identity
// gate forbids for a rename.
if (typeof window !== 'undefined' && !(window as any).__qm_unhandledrejection_swallow_installed) {
    (window as any).__qm_unhandledrejection_swallow_installed = true;
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event?.reason;
        const status = typeof reason?.status === 'number' ? reason.status : null;
        const source = reason?.qmSource;
        if (source === 'apiFetch' && (status === 401 || status === 403)) {
            event.preventDefault();
        }
    });
}
